import React, { useState, useRef, useEffect } from 'react'

export default function BeforeAfterSlider({
  beforeImage,
  afterImage,
  beforeLabel = 'Før',
  afterLabel = 'Efter',
  title,
  description,
  className = ''
}) {
  const [sliderPosition, setSliderPosition] = useState(50)
  const [isDragging, setIsDragging] = useState(false)
  const containerRef = useRef(null)

  const handleMove = (clientX) => {
    if (!containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    const x = clientX - rect.left
    const position = (x / rect.width) * 100
    setSliderPosition(Math.max(0, Math.min(100, position)))
  }

  const handleMouseMove = (e) => {
    if (!isDragging) return
    handleMove(e.clientX)
  }

  const handleTouchMove = (e) => {
    if (!isDragging) return
    if (e.touches.length > 0) {
      handleMove(e.touches[0].clientX)
    }
  }

  useEffect(() => {
    const handleMouseUp = () => setIsDragging(false)
    if (isDragging) {
      window.addEventListener('mouseup', handleMouseUp)
      window.addEventListener('touchend', handleMouseUp)
    }
    return () => {
      window.removeEventListener('mouseup', handleMouseUp)
      window.removeEventListener('touchend', handleMouseUp)
    }
  }, [isDragging])

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      {/* Text Info */}
      {(title || description) && (
        <div className="space-y-2">
          {title && <h3 className="text-xl font-bold text-slate-900 md:text-2xl">{title}</h3>}
          {description && <p className="text-slate-600 text-sm md:text-base leading-relaxed">{description}</p>}
        </div>
      )}

      {/* Slider Container */}
      <div
        ref={containerRef}
        className="relative aspect-16/10 w-full overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 shadow-lg select-none cursor-ew-resize"
        onMouseDown={() => setIsDragging(true)}
        onTouchStart={() => setIsDragging(true)}
        onMouseMove={handleMouseMove}
        onTouchMove={handleTouchMove}
      >
        {/* After Image (Background) */}
        <img
          src={afterImage}
          alt="Efter malerarbejde"
          className="absolute inset-0 h-full w-full object-cover pointer-events-none"
          draggable="false"
        />
        <div className="absolute right-4 top-4 z-10 rounded-full bg-black/30 px-3 py-1 text-xs font-bold text-white backdrop-blur-xs">
          {afterLabel}
        </div>

        {/* Before Image (Foreground, clipped using clip-path) */}
        <div
          className="absolute inset-0 h-full w-full pointer-events-none"
          style={{ clipPath: `polygon(0 0, ${sliderPosition}% 0, ${sliderPosition}% 100%, 0 100%)` }}
        >
          <img
            src={beforeImage}
            alt="Før malerarbejde"
            className="absolute inset-0 h-full w-full object-cover pointer-events-none"
            draggable="false"
          />
          <div className="absolute left-4 top-4 z-10 rounded-full bg-black/30 px-3 py-1 text-xs font-bold text-white backdrop-blur-xs">
            {beforeLabel}
          </div>
        </div>

        {/* Slider Divider Line */}
        <div
          className="absolute bottom-0 top-0 w-1 bg-white cursor-ew-resize z-20 shadow-[0_0_10px_rgba(0,0,0,0.4)]"
          style={{ left: `${sliderPosition}%` }}
        >
          {/* Slider Handle Button */}
          <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-white text-slate-900 shadow-xl flex items-center justify-center border-2 border-slate-200 hover:scale-110 active:scale-95 transition-transform duration-150">
            <i className="fa-solid fa-arrows-left-right text-sm text-slate-700" />
          </div>
        </div>
      </div>
    </div>
  )
}
